import { ApiIntegrations } from './integrations';
import {
  Fee,
  CondoCorpFeature,
  LateFeePolicy,
  LateFeeRunResult,
  ArAgingReport,
  DelinquencyEntry,
  PnlReport,
  BalanceSheet,
  BudgetVarianceRow,
  ReserveFund,
  ReserveFundTransaction,
  Budget,
  BudgetLineItem,
} from '../../types';

export class ApiFeatures extends ApiIntegrations {
  // ============ CONDO CORP FEATURES (PREMIUM FEATURE GATING) ============

  async getCondoFeatures(condoId: string): Promise<CondoCorpFeature[]> {
    return this.request(`/condo-features/${condoId}`);
  }

  /**
   * Sprint 123.1 — tenancy-scoped read of a SINGLE feature flag's enabled state
   * for the caller's own condo. Unlike getCondoFeatures (SUPER_ADMIN-only), this
   * is available to any authenticated role that can access the condo, so a
   * manager/board member can gate a CTA (e.g. AGM-packet Generate) UP-FRONT
   * instead of filling a form then hitting a 403 on submit. Returns false when
   * no feature record exists. Never throws on a "disabled" answer.
   */
  async isCondoFeatureEnabled(condoId: string, featureKey: string): Promise<boolean> {
    const res = await this.request<{ enabled: boolean }>(
      `/condo-features/${condoId}/${featureKey}/enabled`,
    );
    return !!res?.enabled;
  }

  async activateCondoFeature(payload: {
    condoId: string;
    featureKey: string;
    activatedBy: string;
    notes?: string;
  }): Promise<CondoCorpFeature> {
    return this.request('/condo-features/activate', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deactivateCondoFeature(payload: {
    condoId: string;
    featureKey: string;
  }): Promise<void> {
    return this.request('/condo-features/deactivate', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // ============ LATE FEE POLICY ============

  async getLateFeePolicy(condoId: string): Promise<LateFeePolicy | null> {
    try {
      return await this.request<LateFeePolicy>(`/condos/${condoId}/late-fee-policy`);
    } catch {
      return null;
    }
  }

  async upsertLateFeePolicy(condoId: string, policy: Omit<LateFeePolicy, 'id' | 'condoId' | 'createdAt' | 'updatedAt'>): Promise<LateFeePolicy> {
    return this.request<LateFeePolicy>(`/condos/${condoId}/late-fee-policy`, {
      method: 'POST',
      body: JSON.stringify(policy),
    });
  }

  async deleteLateFeePolicy(condoId: string): Promise<void> {
    return this.request<void>(`/condos/${condoId}/late-fee-policy`, { method: 'DELETE' });
  }

  // ============ LATE FEE ENGINE (S17) ============

  /** Dry-run: returns what late fees would be applied without persisting. */
  async previewLateFees(condoId: string): Promise<LateFeeRunResult> {
    return this.request<LateFeeRunResult>(`/condo/late-fees/preview?condoId=${condoId}`);
  }

  /** Live run: applies late fees immediately and returns a result summary. */
  async runLateFees(condoId: string): Promise<LateFeeRunResult> {
    return this.request<LateFeeRunResult>(`/condo/late-fees/run?condoId=${condoId}`, {
      method: 'POST',
    });
  }

  // ============ FEE WAIVING ============

  async waiveFee(feeId: string, reason: string): Promise<Fee> {
    return this.request<Fee>(`/fees/${feeId}/waive`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async getFeesByCondo(condoId: string): Promise<Fee[]> {
    return this.request<Fee[]>(`/fees/condo/${condoId}`);
  }

  // ============ REPORTING ============

  async getArAgingReport(condoId: string): Promise<ArAgingReport> {
    return this.request<ArAgingReport>(`/reports/condo/${condoId}/ar-aging`);
  }

  async getDelinquencyReport(condoId: string): Promise<DelinquencyEntry[]> {
    return this.request<DelinquencyEntry[]>(`/reports/condo/${condoId}/delinquency`);
  }

  async getIncomeStatement(condoId: string, from?: string, to?: string): Promise<PnlReport> {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request<PnlReport>(`/reports/condo/${condoId}/income-statement${qs}`);
  }

  // ============ BALANCE SHEET + BUDGET VARIANCE ============

  async getBalanceSheet(condoId: string): Promise<BalanceSheet> {
    return this.request<BalanceSheet>(`/reports/condo/${condoId}/balance-sheet`);
  }

  async getBudgetVariance(condoId: string, budgetId?: string): Promise<BudgetVarianceRow[]> {
    const qs = budgetId ? `?budgetId=${budgetId}` : '';
    return this.request<BudgetVarianceRow[]>(`/reports/condo/${condoId}/budget-variance${qs}`);
  }

  // ============ RESERVE FUNDS ============

  async getReserveFunds(condoId: string): Promise<ReserveFund[]> {
    return this.request<ReserveFund[]>(`/reserve-funds/condo/${condoId}`);
  }

  async createReserveFund(fund: Omit<ReserveFund, 'id' | 'createdAt' | 'updatedAt'>): Promise<ReserveFund> {
    return this.request<ReserveFund>('/reserve-funds', { method: 'POST', body: JSON.stringify(fund) });
  }

  async updateReserveFund(id: string, fund: Partial<ReserveFund>): Promise<ReserveFund> {
    return this.request<ReserveFund>(`/reserve-funds/${id}`, { method: 'PUT', body: JSON.stringify(fund) });
  }

  async deleteReserveFund(id: string): Promise<void> {
    return this.request<void>(`/reserve-funds/${id}`, { method: 'DELETE' });
  }

  async getReserveFundTransactions(fundId: string): Promise<ReserveFundTransaction[]> {
    return this.request<ReserveFundTransaction[]>(`/reserve-funds/${fundId}/transactions`);
  }

  async addReserveFundTransaction(fundId: string, tx: Omit<ReserveFundTransaction, 'id' | 'createdAt'>): Promise<ReserveFundTransaction> {
    return this.request<ReserveFundTransaction>(`/reserve-funds/${fundId}/transactions`, {
      method: 'POST',
      body: JSON.stringify(tx),
    });
  }

  async deleteReserveFundTransaction(txId: string): Promise<void> {
    return this.request<void>(`/reserve-funds/transactions/${txId}`, { method: 'DELETE' });
  }

  // ============ BUDGETS ============

  async getBudgets(condoId: string): Promise<Budget[]> {
    return this.request<Budget[]>(`/budgets/condo/${condoId}`);
  }

  async createBudget(budget: Omit<Budget, 'id' | 'createdAt' | 'updatedAt'>): Promise<Budget> {
    return this.request<Budget>('/budgets', { method: 'POST', body: JSON.stringify(budget) });
  }

  async updateBudget(id: string, budget: Partial<Budget>): Promise<Budget> {
    return this.request<Budget>(`/budgets/${id}`, { method: 'PUT', body: JSON.stringify(budget) });
  }

  async deleteBudget(id: string): Promise<void> {
    return this.request<void>(`/budgets/${id}`, { method: 'DELETE' });
  }

  async getBudgetLineItems(budgetId: string): Promise<BudgetLineItem[]> {
    return this.request<BudgetLineItem[]>(`/budgets/${budgetId}/line-items`);
  }

  async addBudgetLineItem(budgetId: string, item: Omit<BudgetLineItem, 'id' | 'actualAmount' | 'variance' | 'variancePercent'>): Promise<BudgetLineItem> {
    return this.request<BudgetLineItem>(`/budgets/${budgetId}/line-items`, {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async updateBudgetLineItem(itemId: string, item: Partial<BudgetLineItem>): Promise<BudgetLineItem> {
    return this.request<BudgetLineItem>(`/budgets/line-items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(item),
    });
  }

  async deleteBudgetLineItem(itemId: string): Promise<void> {
    return this.request<void>(`/budgets/line-items/${itemId}`, { method: 'DELETE' });
  }
}
